import React, { ReactNode, useMemo, useRef, useContext } from 'react';
import loadable from '@loadable/component';
import { NafThemeType } from '@naf/theme';
import { themeJustSuper, themeLight, themeVisualBreak } from '@lib/nafTheme';
import { useSpring, animated, config } from '@react-spring/web';
import { ThemeContextValue } from '@lib/ThemeContext';
import { PromotionTrackingContext } from '@lib/PromotionTrackingContext';
import { SectionBlockType } from '../../../../types/CategoryAppType';
import { ThemeVariants } from '../../../../types/themes';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';
import { ReferencedComponentBlock } from '../block-content/components/types/components/ReferencedComponentSerializer';
import { Shortcuts } from '../../pages/FrontPage/components/Shortcuts/Shortcuts';
import { MainEntries } from '../../pages/FrontPage/components/MainEntries/MainEntries';
import { MembershipBenefitHighlightListBlock } from './blocks/MembershipBenefitHighlightListBlock';
import CustomBenefitListBlock from './blocks/CustomBenefitListBlock/CustomBenefitListBlock';

const Timeline = loadable(() => import('./blocks/Timeline/Timeline'));
const ImageTitleAndLinkList = loadable(() => import('./blocks/ImageTitleAndLinkList'));
const SecondaryEntriesBlock = loadable(() => import('./blocks/SecondaryEntriesBlock'));
const StepByStepList = loadable(() => import('./blocks/StepByStepList'));

const EmbeddedArticleBlock = loadable(() => import('./blocks/EmbeddedArticle/EmbeddedArticleBlock'), {
  resolveComponent: (m) => m.EmbeddedArticleBlock,
});

const TravelSuggestions = loadable(() => import('./blocks/TravelSuggestions/TravelSuggestions'), {
  resolveComponent: (m) => m.TravelSuggestions,
});

const TabBlock = loadable(() => import('./blocks/TabBlock'), { resolveComponent: (m) => m.TabBlock });

const MembershipBenefitListBlock = loadable(() => import('./blocks/MembershipBenefitListBlock'), {
  resolveComponent: (m) => m.MembershipBenefitListBlock,
});

const SingleNavigationCardBlock = loadable(() => import('./ColumnContent/SingleNavigationCardBlock'), {
  resolveComponent: (m) => m.SingleNavigationCardBlock,
});

const VerticalArticleBlock = loadable(() => import('./ColumnContent/VerticalArticleBlock'), {
  resolveComponent: (m) => m.VerticalArticleBlock,
});

const ColumnBlock = loadable(() => import('./ColumnBlock'), { resolveComponent: (m) => m.ColumnBlock });

const FeaturedVehicleTestResult = loadable(() => import('./FeaturedVehicleTestResult'), {
  resolveComponent: (m) => m.FeaturedVehicleTestResult,
});

const Results = loadable(() => import('./Testshowcase/Results/Results'), { resolveComponent: (m) => m.Results });

const FeaturedBenefitBlock = loadable(
  () => import('./blocks/FeaturedBenefitBlocks/FeaturedBenefitBlock/FeaturedBenefitBlock'),
  { resolveComponent: (m) => m.FeaturedBenefitBlock },
);

const IframeBlock = loadable(() => import('./blocks/IframeBlock'), { resolveComponent: (m) => m.IframeBlock });

const InputWithImageLinkBlock = loadable(() => import('./blocks/InputWithImageLinkBlock/InputWithImageLinkBlock'), {
  resolveComponent: (m) => m.InputWithImageLinkBlock,
});

const ColorSection = loadable(() => import('../../pages/TopicPageApp/ColorSection/ColorSection'), {
  resolveComponent: (m) => m.ColorSection,
});

const PortraitsWithTextBlock = loadable(() => import('./blocks/PortraitsWithTextBlock'), {
  resolveComponent: (m) => m.PortraitsWithTextBlock,
});

const MembershipBenefitsBlock = loadable(() => import('./blocks/MembershipBenefits'), {
  resolveComponent: (m) => m.MembershipBenefitsBlock,
});

const CampaignBlock = loadable(() => import('./blocks/Campaign'), { resolveComponent: (m) => m.CampaignBlock });

const TextAndImageBlock = loadable(() => import('./blocks/TextAndImageBlock'), {
  resolveComponent: (m) => m.TextAndImageBlock,
});

const NewsBlock = loadable(() => import('./blocks/News/NewsBlock'), { resolveComponent: (m) => m.NewsBlock });

const LatestArticlesBlock = loadable(() => import('./blocks/LatestArticleBlock'), {
  resolveComponent: (m) => m.LatestArticlesBlock,
});

const HeroBlock = loadable(() => import('./blocks/HeroBlock'), { resolveComponent: (m) => m.HeroBlock });

const CategoryListWithScrollBlock = loadable(() => import('./blocks/CategoryListWithScroll'), {
  resolveComponent: (m) => m.CategoryListWithScrollBlock,
});

const ThreeColumnBlock = loadable(() => import('./blocks/ThreeColumnBlock'), {
  resolveComponent: (m) => m.ThreeColumnBlock,
});

const AccordionBlock = loadable(() => import('./blocks/AccordionBlock'), { resolveComponent: (m) => m.AccordionBlock });

const AccordionListBlock = loadable(() => import('./blocks/AccordionListBlock'), {
  resolveComponent: (m) => m.AccordionListBlock,
});

const NewsfeedBlock = loadable(() => import('./blocks/NewsFeedBlock/NewsfeedBlock'), {
  resolveComponent: (m) => m.NewsfeedBlock,
});

const ThemeHeroBlock = loadable(() => import('./blocks/ThemeHeroBlock/ThemeHeroBlock'), {
  resolveComponent: (m) => m.ThemeHeroBlock,
});

const CountdownBlock = loadable(() => import('./blocks/CountdownBlock/CountdownBlock'), {
  resolveComponent: (m) => m.CountdownBlock,
});

const QnaFeedBlock = loadable(() => import('./blocks/QnaFeedBlock'), { resolveComponent: (m) => m.QnaFeedBlock });

const ScrollableTextBlocksWithBackgroundImages = loadable(
  () => import('./blocks/Parallax/ScrollableTextBlocksWithBackgroundImages'),
  { resolveComponent: (m) => m.ScrollableTextBlocksWithBackgroundImages },
);

const ShowcaseBlock = loadable(() => import('./Testshowcase/ShowcaseBlock'), {
  resolveComponent: (m) => m.ShowcaseBlock,
});

const LivestreamBlock = loadable(() => import('./blocks/LivestreamBlock'), {
  resolveComponent: (m) => m.LivestreamBlock,
});

const ShortsCarouselBlock = loadable(() => import('./blocks/ShortsCarouselBlock'), {
  resolveComponent: (m) => m.ShortsCarouselBlock,
});

const RouteCharts = loadable(() => import('./blocks/Charts/RouteCharts'), { resolveComponent: (m) => m.RouteCharts });

const GoogleMapsWrapper = loadable(() => import('./blocks/Charts/GoogleMapsWrapper'), {
  resolveComponent: (m) => m.GoogleMapsWrapper,
});

const TextBlock = loadable(() => import('./blocks/TextBlock'), { resolveComponent: (m) => m.TextBlock });

const KeyStatisticsBlock = loadable(() => import('./blocks/KeyStatistics/KeyStatisticsBlock'), {
  resolveComponent: (m) => m.KeyStatisticsBlock,
});

const MultiRowArticleBlock = loadable(() => import('./blocks/MultiRowArticleBlock'), {
  resolveComponent: (m) => m.MultiRowArticleBlock,
});

const NavigationLinksBlock = loadable(() => import('./blocks/NavigationLinksBlock'), {
  resolveComponent: (m) => m.NavigationLinksBlock,
});

const ImageLinksBlock = loadable(() => import('./blocks/ImageLinks/ImageLinksBlock'), {
  resolveComponent: (m) => m.ImageLinksBlock,
});

const DocumentSnapshots = loadable(() => import('./blocks/DocumentSnapshots'), {
  resolveComponent: (m) => m.DocumentSnapshots,
});

const BilguideSearchBlock = loadable(() => import('./blocks/BilguideSearchBlock'), {
  resolveComponent: (m) => m.BilguideSearchBlock,
});

const FeaturedModelBlock = loadable(() => import('./FeaturedModelBlock/FeaturedModelBlock'), {
  resolveComponent: (m) => m.FeaturedModelBlock,
});

const TwoArticleBlock = loadable(() => import('./blocks/TwoArticleBlock'), {
  resolveComponent: (m) => m.TwoArticleBlock,
});

const TrialMembershipNavigationCardContainerBlock = loadable(
  () => import('./blocks/TrialMembership/welcomeCard/TrialMembershipNavigationCardContainerBlock'),
  { resolveComponent: (m) => m.TrialMembershipNavigationCardContainerBlock },
);

const BodyCtaAndBodyBannerBlock = loadable(() => import('./blocks/TrialMembership/banner/BodyCtaAndBodyBannerBlock'), {
  resolveComponent: (m) => m.BodyCtaAndBodyBannerBlock,
});

const QRCodeBlock = loadable(() => import('./blocks/QRCode'), { resolveComponent: (m) => m.QRCodeBlock });

const VippsLoginBlock = loadable(() => import('./blocks/VippsLogin'), { resolveComponent: (m) => m.VippsLoginBlock });

const ProductsLoggedInActivationFormBlock = loadable(() => import('./blocks/ProductsLoggedInActivationForm'), {
  resolveComponent: (m) => m.ProductsLoggedInActivationForm,
});

const TrialMembershipWelcomeCardBlock = loadable(
  () => import('./blocks/TrialMembership/welcomeCard/TrialMembershipWelcomeCardBlock'),
  { resolveComponent: (m) => m.TrialMembershipWelcomeCardBlock },
);

const CardCarouselBlock = loadable(() => import('./blocks/TrialMembership/cardCarousel/CardCarouselBlock'), {
  resolveComponent: (m) => m.CardCarouselBlock,
});

const CompareMembershipBlock = loadable(() => import('./blocks/CompareMembershipBlock/CompareMembershipBlock'), {
  resolveComponent: (m) => m.CompareMembershipBlock,
});

const ConsumerGuidesBlock = loadable(() => import('./blocks/ConsumerGuideBlock/ConsumerGuideBlock'), {
  resolveComponent: (m) => m.ConsumerGuideBlock,
});

const ModularButtonBlock = loadable(() => import('./blocks/ModularButtonBlock'), {
  resolveComponent: (m) => m.ModularButtonBlock,
});

const ConsumerTestProductList = loadable(
  () => import('../block-content/components/types/components/ConsumerTestProductList/ConsumerTestProductList'),
  { resolveComponent: (m) => m.ConsumerTestProductList },
);

const FAQGlobalReferenceBlock = loadable(() => import('./blocks/FAQGlobalReferenceBlock/FAQGlobalReferenceBlock'), {
  resolveComponent: (m) => m.FAQGlobalReferenceBlock,
});

const OtherInformationBlock = loadable(() => import('./blocks/OtherInformationBlock'), {
  resolveComponent: (m) => m.OtherInformationBlock,
});

const NafLocationsMapBlock = loadable(() => import('./blocks/NafLocationsMapBlock/NafLocationsMapBlock'), {
  resolveComponent: (m) => m.NafLocationsMapBlock,
});

const ProductCardListBlock = loadable(() => import('./blocks/ProductCardListBlock'), {
  resolveComponent: (m) => m.ProductCardListBlock,
});

interface Props {
  content?: (SectionBlockType | null | undefined)[];
  activateContextThemeSwitch?: boolean;
}

const isSectionBlock = (block: SectionBlockType | null | undefined): block is SectionBlockType => Boolean(block?.type);

const mapBlockTheme = (block?: SectionBlockType, theme?: NafThemeType) => {
  // Assume this functionallity is only used for timeline component with parallax effect
  if (block?.type === 'timeline') {
    switch (block.colorScheme) {
      case ThemeVariants.Standard:
        return themeLight;
      case ThemeVariants.VisualBreak:
        return themeVisualBreak;
      case ThemeVariants.JustSuper:
        return themeJustSuper;
      default:
        return themeLight;
    }
  }
  return theme || themeLight;
};

const Section = ({
  block,
  children,
  currentTheme,
}: {
  block: SectionBlockType;
  children: ReactNode;
  currentTheme?: NafThemeType;
}) => <ColorSection theme={mapBlockTheme(block, currentTheme)}>{children}</ColorSection>;

const Blocks = ({ block }: { block: SectionBlockType }) => {
  switch (block.type) {
    case 'news':
      return <NewsBlock data={block.data} />;
    case 'membershipBenefits':
      return <MembershipBenefitsBlock benefits={block.data} total={block.total} title={block.title} />;
    case 'campaignBlock':
      return <CampaignBlock data={block.data} />;
    case 'campaignGlobalReference':
      return <CampaignBlock data={block.data} />;
    case 'textAndImage':
      return <TextAndImageBlock block={block} />;
    case 'latestArticles':
      return <LatestArticlesBlock block={block} />;
    case 'hero':
      return <HeroBlock block={block} />;
    case 'categoryScrollList':
      return <CategoryListWithScrollBlock block={block} />;
    case 'threeColumn':
      return <ThreeColumnBlock block={block} />;
    case 'accordionBlock':
      return <AccordionBlock block={block} />;
    case 'accordionListBlock':
      return <AccordionListBlock block={block} />;
    case 'timeline':
      return <Timeline block={block} />;
    case 'countdownBlock':
      return <CountdownBlock block={block} />;
    case 'newsfeedBlock':
      return <NewsfeedBlock block={block.data} />;
    case 'questionsAndAnswersBlock':
      return <QnaFeedBlock block={block} />;
    case 'heroWithTitleIngressSmallImageAndCta':
      return <ThemeHeroBlock block={block} />;
    case 'scrollableTextBlocksWithBackgroundImages':
      return <ScrollableTextBlocksWithBackgroundImages key={block.key} block={block} />;
    case 'testshowcase':
      return <ShowcaseBlock block={block} />;
    case 'livestreamBlock':
      return <LivestreamBlock key={block.key} block={block} />;
    case 'youtubeShorts':
      return <ShortsCarouselBlock key={block.key} block={block} />;
    case 'textBlock':
      return <TextBlock block={block} />;
    case 'navigationLinks':
      return <NavigationLinksBlock block={block} />;
    case 'keyStatistics':
      return <KeyStatisticsBlock block={block} />;
    case 'otherInformationBlock':
      return <OtherInformationBlock block={block} />;
    case 'nafLocationsMap':
      return <NafLocationsMapBlock block={block} />;
    case 'productCardListBlock':
      return <ProductCardListBlock block={block} />;
    case 'routeBlock':
      return (
        <GoogleMapsWrapper>
          <RouteCharts block={block} />
        </GoogleMapsWrapper>
      );
    case 'multiRowArticleBlock':
      return <MultiRowArticleBlock block={block} />;
    case 'imageLinks':
      return <ImageLinksBlock block={block} />;
    case 'portraitsWithText':
      return <PortraitsWithTextBlock block={block} />;
    case 'inputWithImageLinkBlock':
      return <InputWithImageLinkBlock block={block} />;
    case 'iframeBlock':
      return <IframeBlock block={block} />;
    case 'featuredBenefitCampaign':
      return <FeaturedBenefitBlock block={block} />;
    case 'testResultBlock':
      return <Results block={block} />;
    case 'featuredVehicleTestResult':
      return <FeaturedVehicleTestResult block={block} />;
    case 'columnBlock':
      return <ColumnBlock block={block} />;
    case 'simpleArticle':
      return <VerticalArticleBlock block={block} />;
    case 'entry':
      return <SingleNavigationCardBlock size="large" block={block} />;
    case 'imageTitleAndLinkList':
      return <ImageTitleAndLinkList block={block} />;
    case 'stepByStepList':
      return <StepByStepList block={block} />;
    case 'membershipBenefitListBlock':
      return <MembershipBenefitListBlock />;
    case 'documentSnapshots':
      return <DocumentSnapshots block={block} />;
    case 'tabsSection':
      return <TabBlock block={block} />;
    case 'tabsSectionMembershipBenefit':
      return <TabBlock block={block} />;
    case 'secondaryEntriesBlock':
      return <SecondaryEntriesBlock block={block} />;
    case 'bilguideSearchBlock':
      return <BilguideSearchBlock block={block} />;
    case 'featuredModelBlock':
      return <FeaturedModelBlock block={block} />;
    case 'travelSuggestionsBlock':
      return <TravelSuggestions />;
    case 'nafnoArticleEmbedded':
      return <EmbeddedArticleBlock block={block} />;
    case 'referencedComponent':
      return <ReferencedComponentBlock value={block} />;
    case 'twoArticleBlock':
      return <TwoArticleBlock node={block} />;
    case 'trialMembershipNavigationCardContainerBlock':
      return <TrialMembershipNavigationCardContainerBlock block={block} />;
    case 'bodyCtaAndBodyBannerBlock':
      return <BodyCtaAndBodyBannerBlock block={block} />;
    case 'cardCarouselBlock':
      return <CardCarouselBlock block={block} />;
    case 'mainEntries':
      return <MainEntries node={block} />;
    case 'shortcuts':
      return <Shortcuts node={block} />;
    case 'qrCodeReference':
      return <QRCodeBlock node={block} />;
    case 'vippsLogin':
      return <VippsLoginBlock value={block} />;
    case 'trialMembershipWelcomeCardBlock':
      return <TrialMembershipWelcomeCardBlock queryParam={block.queryParam} />;
    case 'productsLoggedInActivationForm':
      return <ProductsLoggedInActivationFormBlock node={block} />;
    case 'compareMembershipBlock':
      return <CompareMembershipBlock block={block} />;
    case 'consumerGuideBlock':
      return <ConsumerGuidesBlock />;
    case 'membershipBenefitHighlights':
      return <MembershipBenefitHighlightListBlock block={block} />;
    case 'modularButtons':
      return <ModularButtonBlock block={block} />;
    case 'consumerTestProductList':
      return <ConsumerTestProductList value={block} />;
    case 'faqGlobalReference':
      return <FAQGlobalReferenceBlock block={block} />;
    case 'customBenefitListBlock':
      return <CustomBenefitListBlock block={block} />;
    default:
      return null;
  }
};

export const SectionBlocks = ({ content, activateContextThemeSwitch }: Props) => {
  const theme = useContext(ThemeContextValue);
  const promotionTracking = useContext(PromotionTrackingContext);
  const validContent = useMemo(() => content?.filter(isSectionBlock) ?? [], [content]);
  const hasCompareMembershipBlock = useMemo(
    () => validContent.some((block) => block.type === 'compareMembershipBlock'),
    [validContent],
  );

  useIsomorphicLayoutEffect(() => {
    if (!promotionTracking) {
      return;
    }

    promotionTracking.setDisablePromotionTracking(hasCompareMembershipBlock);

    // eslint-disable-next-line consistent-return
    return () => {
      promotionTracking.setDisablePromotionTracking(false);
    };
  }, [promotionTracking, hasCompareMembershipBlock]);

  if (validContent.length > 0) {
    return (
      <>
        {validContent.map((block) =>
          activateContextThemeSwitch ? (
            <Section block={block} key={block.key} currentTheme={theme}>
              <Blocks block={block} />
            </Section>
          ) : (
            <Blocks block={block} key={block.key} />
          ),
        )}
      </>
    );
  }
  return null;
};

export const VerticallyFadingSectionBlocks = ({ content, activateContextThemeSwitch }: Props) => {
  const theme = useContext(ThemeContextValue);
  const validContent = useMemo(() => content?.filter(isSectionBlock) ?? [], [content]);

  if (validContent.length > 0) {
    return (
      <>
        {validContent.map((block) =>
          activateContextThemeSwitch ? (
            <Section block={block} key={block.key} currentTheme={theme}>
              <VerticallyFadingBlock block={block} />
            </Section>
          ) : (
            <VerticallyFadingBlock key={block.key} block={block} />
          ),
        )}
      </>
    );
  }
  return null;
};

const VerticallyFadingBlock = ({ block }: { block: SectionBlockType }) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dataRef = useIntersectionObserver(triggerRef, { freezeOnceVisible: true });

  const [props] = useSpring(
    () => ({
      config: config.gentle,
      from: { opacity: 0, y: 40 },
      to: {
        opacity: dataRef?.isIntersecting ? 1 : 0,
        y: dataRef?.isIntersecting ? 0 : 40,
      },
    }),
    [dataRef],
  );

  return (
    <animated.div ref={triggerRef} style={props}>
      <Blocks block={block} />
    </animated.div>
  );
};
